import React, { useEffect, useRef } from 'react';
import { GradientText } from '../components/ui/GradientText';
import { Brain, Palette, AppWindow, MessageSquare, BarChart2, Sparkles, Zap, Lock, Globe2, Mail, Phone, BellRing, BarChart, Users, Link, Share2, Rocket, LineChart, Settings, ShoppingCart, Store, Package, Layers, Database, Gift, RefreshCw, Plus } from 'lucide-react';
import Footer from '../components/Footer';
import Hero from '../components/Hero';
import { useTranslation } from '../hooks/useTranslation';
import { FloatingWidget } from '../components/widget/FloatingWidget';

// Floating elements animation component
const FloatingElements = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const elements = Array.from(container.children) as HTMLElement[];
    
    elements.forEach(element => {
      // Random starting position
      const x = Math.random() * 100;
      const y = Math.random() * 100;
      
      // Random animation duration between 15-30s
      const duration = 15 + Math.random() * 15;
      
      // Random direction
      const dirX = Math.random() > 0.5 ? 1 : -1;
      const dirY = Math.random() > 0.5 ? 1 : -1;
      
      element.style.setProperty('--x', `${x}%`);
      element.style.setProperty('--y', `${y}%`);
      element.style.setProperty('--duration', `${duration}s`);
      element.style.setProperty('--dir-x', dirX.toString());
      element.style.setProperty('--dir-y', dirY.toString());
    });
  }, []);
  
  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
      {Array.from({ length: 15 }).map((_, i) => (
        <div 
          key={i}
          className="absolute w-16 h-16 rounded-full bg-gradient-to-br animate-float"
          style={{
            backgroundImage: `radial-gradient(circle, ${i % 2 === 0 ? 'rgba(124,58,237,0.2)' : 'rgba(45,212,191,0.2)'}, transparent 70%)`,
            animation: 'float var(--duration) ease-in-out infinite alternate',
            left: 'var(--x)',
            top: 'var(--y)',
            animationDirection: i % 2 === 0 ? 'alternate' : 'alternate-reverse',
          }}
        />
      ))}
    </div>
  );
};

export default function Home() {
  const { t } = useTranslation();

  return (
    <div className="bg-black">
      {/* Hero Section */}
      <Hero />

      {/* Demo Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Fond moderne avec mesh gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(124,58,237,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(45,212,191,0.15),transparent_50%)]" />
        
        {/* Effet de cercle lumineux en arrière-plan */}
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-violet-500/10 to-transparent blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
              <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
              Interactive Demo
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">
              <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">{t('pages.home.demo.title')}</GradientText>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('pages.home.demo.description')}
            </p>
          </div>

          <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-1 border border-white/10 shadow-[0_0_15px_rgba(124,58,237,0.1)]">
            <div className="w-full">
              <iframe 
                src="https://botmaker-agency.ai/widget?id=cd2d2829-7526-482a-a97a-3550d87c8d5f&theme=dark"
                className="w-full rounded-xl md:h-[700px] h-[85vh] max-h-[700px]"
                style={{ border: 'none' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Lead Detection Section */}
      <section className="relative py-32 overflow-hidden">
        {/* Fond moderne avec mesh gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(124,58,237,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(45,212,191,0.15),transparent_50%)]" />
        
        {/* Effet de cercle lumineux en arrière-plan */}
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-tl from-cyan-500/10 to-transparent blur-3xl" />
        
        {/* Grille stylisée en arrière-plan */}
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-[0.03]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
              <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
              Lead Capture
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              {t('pages.home.leadDetection.title')}{' '}
              <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">{t('pages.home.leadDetection.highlight')}</GradientText>{' '}
              {t('pages.home.leadDetection.suffix')}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('pages.home.leadDetection.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-6 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)]">
                <div className="flex items-center gap-5 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <Mail className="w-6 h-6 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{t('pages.home.leadDetection.features.emails.title')}</h3>
                    <p className="text-gray-300 mt-1">{t('pages.home.leadDetection.features.emails.description')}</p>
                  </div>
                </div>
              </div>

              <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-6 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)]">
                <div className="flex items-center gap-5 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <Phone className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{t('pages.home.leadDetection.features.phones.title')}</h3>
                    <p className="text-gray-300 mt-1">{t('pages.home.leadDetection.features.phones.description')}</p>
                  </div>
                </div>
              </div>

              <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-6 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)]">
                <div className="flex items-center gap-5 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <BellRing className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{t('pages.home.leadDetection.features.notifications.title')}</h3>
                    <p className="text-gray-300 mt-1">{t('pages.home.leadDetection.features.notifications.description')}</p>
                  </div>
                </div>
              </div>

              <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-6 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)]">
                <div className="flex items-center gap-5 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <Zap className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{t('pages.home.leadDetection.features.crm.title')}</h3>
                    <p className="text-gray-300 mt-1">{t('pages.home.leadDetection.features.crm.description')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-violet-600/30 to-cyan-500/30 blur-xl opacity-50"></div>
              <div className="relative bg-black/60 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&q=80&w=1000"
                    alt="Lead Detection"
                  className="rounded-lg shadow-lg"
                  />
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-cyan-500"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Séparateur subtil en bas */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent opacity-30"></div>
      </section>

      {/* Design Section */}
      <section className="relative py-28 overflow-hidden">
        {/* Fond moderne avec mesh gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,58,237,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(45,212,191,0.15),transparent_50%)]" />
        
        {/* Effet de cercle lumineux en arrière-plan */}
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-violet-500/10 to-transparent blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div className="mb-12 lg:mb-0">
              <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
                <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
                Customization
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
                {t('pages.home.design.title')}{' '}
                <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">{t('pages.home.design.highlight')}</GradientText>{' '}
                {t('pages.home.design.suffix')}
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-xl">
                {t('pages.home.design.description')}
              </p>
              <ul className="space-y-5 text-gray-200">
                <li className="flex items-center gap-4 bg-black/40 backdrop-blur-xl p-4 rounded-xl border border-white/10 transition-all hover:border-violet-500/30 duration-300">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <Palette className="w-5 h-5 text-violet-400" />
                  </div>
                  <span className="text-lg">{t('pages.home.design.features.colors')}</span>
                </li>
                <li className="flex items-center gap-4 bg-black/40 backdrop-blur-xl p-4 rounded-xl border border-white/10 transition-all hover:border-violet-500/30 duration-300">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <MessageSquare className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span className="text-lg">{t('pages.home.design.features.messages')}</span>
                </li>
                <li className="flex items-center gap-4 bg-black/40 backdrop-blur-xl p-4 rounded-xl border border-white/10 transition-all hover:border-violet-500/30 duration-300">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <Sparkles className="w-5 h-5 text-indigo-400" />
                  </div>
                  <span className="text-lg">{t('pages.home.design.features.themes')}</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-violet-600/30 to-cyan-500/30 blur-xl opacity-50"></div>
              <div className="relative bg-black/60 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl transform transition-transform hover:-translate-y-1 duration-300">
                  <img 
                    src="https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?auto=format&fit=crop&q=80&w=1000"
                    alt="Design Interface"
                  className="rounded-lg shadow-lg"
                  />
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-cyan-500"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Séparateur subtil en bas */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-30"></div>
      </section>

      {/* AI Section */}
      <section className="relative py-28 overflow-hidden">
        {/* Fond moderne avec mesh gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(124,58,237,0.2),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(45,212,191,0.15),transparent_50%)]" />
        
        {/* Effet de cercle lumineux en arrière-plan */}
        <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-cyan-500/10 to-transparent blur-3xl" />
        
        {/* Grille stylisée en arrière-plan */}
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-[0.03]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div className="relative order-2 mb-12 lg:mb-0">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-violet-600/30 to-cyan-500/30 blur-xl opacity-50"></div>
              <div className="relative bg-black/60 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl transform transition-transform hover:-translate-y-1 duration-300">
                  <img 
                    src="https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=1000"
                    alt="AI Configuration"
                  className="rounded-lg shadow-lg"
                  />
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-cyan-500"></div>
              </div>
            </div>
            <div className="order-1">
              <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
                <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
                AI Setup
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
                {t('pages.home.ai.title')}{' '}
                <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">{t('pages.home.ai.highlight')}</GradientText>{' '}
                {t('pages.home.ai.suffix')}
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-xl">
                {t('pages.home.ai.description')}
              </p>
              <ul className="space-y-5 text-gray-200">
                <li className="flex items-center gap-4 bg-black/40 backdrop-blur-xl p-4 rounded-xl border border-white/10 transition-all hover:border-violet-500/30 duration-300">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <Brain className="w-5 h-5 text-violet-400" />
                  </div>
                  <span className="text-lg">{t('pages.home.ai.features.personalization')}</span>
                </li>
                <li className="flex items-center gap-4 bg-black/40 backdrop-blur-xl p-4 rounded-xl border border-white/10 transition-all hover:border-violet-500/30 duration-300">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <Lock className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span className="text-lg">{t('pages.home.ai.features.security')}</span>
                </li>
                <li className="flex items-center gap-4 bg-black/40 backdrop-blur-xl p-4 rounded-xl border border-white/10 transition-all hover:border-violet-500/30 duration-300">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                    <Globe2 className="w-5 h-5 text-indigo-400" />
                  </div>
                  <span className="text-lg">{t('pages.home.ai.features.multilingual')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Séparateur subtil en bas */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent opacity-30"></div>
      </section>

      {/* Mini Apps Section */}
      <section className="relative py-28 overflow-hidden">
        {/* Fond moderne avec mesh gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(124,58,237,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(45,212,191,0.2),transparent_60%)]" />
        
        {/* Effet de cercle lumineux en arrière-plan */}
        <div className="absolute top-1/3 right-1/3 w-[450px] h-[450px] rounded-full bg-gradient-to-br from-violet-500/10 to-transparent blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div className="mb-12 lg:mb-0">
              <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
                <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
                All-in-One Platform
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
                {t('pages.home.miniApps.title')}{' '}
                <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">{t('pages.home.miniApps.highlight')}</GradientText>{' '}
                {t('pages.home.miniApps.suffix')}
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-xl">
                {t('pages.home.miniApps.description')}
              </p>
              <div className="grid grid-cols-2 gap-5 mb-8">
                <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-5 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-4 shadow-inner">
                    <AppWindow className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{t('pages.home.miniApps.apps.calendar.title')}</h3>
                  <p className="text-gray-300 text-sm">{t('pages.home.miniApps.apps.calendar.description')}</p>
                </div>
                <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-5 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-4 shadow-inner">
                    <Zap className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{t('pages.home.miniApps.apps.products.title')}</h3>
                  <p className="text-gray-300 text-sm">{t('pages.home.miniApps.apps.products.description')}</p>
                </div>
                <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-5 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-4 shadow-inner">
                    <BarChart2 className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{t('pages.home.miniApps.apps.services.title')}</h3>
                  <p className="text-gray-300 text-sm">{t('pages.home.miniApps.apps.services.description')}</p>
                </div>
                <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-5 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-4 shadow-inner">
                    <Sparkles className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{t('pages.home.miniApps.apps.gallery.title')}</h3>
                  <p className="text-gray-300 text-sm">{t('pages.home.miniApps.apps.gallery.description')}</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-violet-600/30 to-cyan-500/30 blur-xl opacity-50"></div>
              <div className="relative bg-black/60 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl transform transition-transform hover:-translate-y-1 duration-300">
                  <img 
                    src="https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&q=80&w=1000" 
                    alt="Mini Apps"
                  className="rounded-lg shadow-lg"
                  />
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-cyan-500"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Séparateur subtil en bas */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-30"></div>
      </section>

      {/* Multilingual Section */}
      <section className="relative py-28 overflow-hidden">
        {/* Fond moderne avec mesh gradient et motif */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.08),transparent_70%)]" />
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-[0.03]" />
        
        {/* Effets de cercles lumineux en arrière-plan */}
        <div className="absolute top-1/4 left-20 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-violet-500/10 to-transparent blur-3xl" />
        <div className="absolute bottom-1/4 right-20 w-[350px] h-[350px] rounded-full bg-gradient-to-tr from-cyan-500/10 to-transparent blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
              <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
              Global Reach
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              {t('pages.home.multilingual.title')}{' '}
              <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">{t('pages.home.multilingual.highlight')}</GradientText>{' '}
              {t('pages.home.multilingual.suffix')}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-16">
              {t('pages.home.multilingual.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                <Globe2 className="w-8 h-8 text-violet-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.multilingual.features.translation')}</h3>
              <p className="text-gray-300 text-lg leading-relaxed">{t('pages.home.multilingual.features.choice')}</p>
            </div>

            <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                <MessageSquare className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.multilingual.features.choice')}</h3>
              <p className="text-gray-300 text-lg leading-relaxed">{t('pages.home.multilingual.features.configuration')}</p>
            </div>

            <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                <Settings className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.multilingual.features.configuration')}</h3>
              <p className="text-gray-300 text-lg leading-relaxed">{t('pages.home.multilingual.features.translation')}</p>
            </div>
          </div>
        </div>
        
        {/* Séparateur subtil en bas */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent opacity-30"></div>
      </section>

      {/* Analytics Section - NEW */}
      <section className="relative py-28 overflow-hidden">
        {/* Fond moderne avec mesh gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,58,237,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(45,212,191,0.15),transparent_50%)]" />
        
        {/* Effet de cercle lumineux en arrière-plan */}
        <div className="absolute bottom-1/3 left-1/3 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-violet-500/10 to-transparent blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
              <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
              Powerful Insights
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              {t('pages.home.analytics.title')}{' '}
              <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">{t('pages.home.analytics.highlight')}</GradientText>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('pages.home.analytics.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                  <BarChart className="w-8 h-8 text-violet-400" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.analytics.features.stats.title')}</h3>
                <p className="text-gray-300 text-lg leading-relaxed">{t('pages.home.analytics.features.stats.description')}</p>
              </div>

              <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                  <Users className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.analytics.features.segments.title')}</h3>
                <p className="text-gray-300 text-lg leading-relaxed">{t('pages.home.analytics.features.segments.description')}</p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-violet-600/30 to-cyan-500/30 blur-xl opacity-50"></div>
              <div className="relative bg-black/60 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl transform transition-transform hover:-translate-y-1 duration-300">
                  <img 
                    src="https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&q=80&w=1000" 
                    alt="Analytics Dashboard"
                  className="rounded-lg shadow-lg"
                />
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-cyan-500"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Séparateur subtil en bas */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent opacity-30"></div>
      </section>

      {/* Integrations Section - NEW */}
      <section className="relative py-28 overflow-hidden">
        {/* Modern background with animations */}
        <FloatingElements />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(124,58,237,0.1),transparent_70%)]" />
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-[0.03]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80 animate-bounce-slow">
              <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
              {t('pages.home.integrations.badge')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              <span className="relative inline-block">
                <span className="absolute -top-6 -left-14 text-sm bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400 px-2 py-1 rounded-full animate-pulse">+1000</span>
                <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">{t('pages.home.integrations.title')}</GradientText>
              </span> {t('pages.home.integrations.subtitle')}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10">
              {t('pages.home.integrations.description')}
            </p>
          </div>

          {/* Logo Marquee Section with real-like logos */}
          <div className="mb-16 relative">
            <div className="absolute top-0 bottom-0 left-0 w-20 z-10 bg-gradient-to-r from-black to-transparent pointer-events-none"></div>
            <div className="absolute top-0 bottom-0 right-0 w-20 z-10 bg-gradient-to-l from-black to-transparent pointer-events-none"></div>
            
            {/* First Marquee - Left to Right - Faster - Popular CRMs */}
            <div className="overflow-hidden relative py-8 mb-2">
              <div className="flex space-x-8 whitespace-nowrap animate-marquee-fast">
                {/* First set of logos */}
                {[
                  { name: 'Salesforce', logo: 'https://www.vectorlogo.zone/logos/salesforce/salesforce-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'HubSpot', logo: 'https://www.vectorlogo.zone/logos/hubspot/hubspot-ar21.svg', bgColor: 'bg-orange-50' },
                  { name: 'WhatsApp', logo: 'https://www.vectorlogo.zone/logos/whatsapp/whatsapp-ar21.svg', bgColor: 'bg-green-50' },
                  { name: 'Meta', logo: 'https://www.vectorlogo.zone/logos/meta/meta-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Slack', logo: 'https://www.vectorlogo.zone/logos/slack/slack-ar21.svg', bgColor: 'bg-purple-50' },
                  { name: 'Odoo', logo: 'https://www.vectorlogo.zone/logos/odoo/odoo-ar21.svg', bgColor: 'bg-gray-50' },
                  { name: 'Zapier', logo: 'https://www.vectorlogo.zone/logos/zapier/zapier-ar21.svg', bgColor: 'bg-orange-50' },
                  { name: 'Make', logo: 'https://seeklogo.com/images/I/integromat-logo-2B5C771360-seeklogo.com.png', bgColor: 'bg-blue-50' },
                  { name: 'Instagram', logo: 'https://www.vectorlogo.zone/logos/instagram/instagram-ar21.svg', bgColor: 'bg-pink-50' },
                  { name: 'Wix', logo: 'https://www.vectorlogo.zone/logos/wix/wix-ar21.svg', bgColor: 'bg-gray-50' },
                ].map((item, i) => (
                  <div key={i} className="flex-shrink-0 w-36 h-24 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] transform hover:scale-110 overflow-hidden">
                    <div className={`w-full h-full flex items-center justify-center ${item.bgColor} rounded-md p-2`}>
                      <img src={item.logo} alt={item.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  </div>
                ))}
                
                {/* Duplicate logos for infinite scroll effect */}
                {[
                  { name: 'Salesforce', logo: 'https://www.vectorlogo.zone/logos/salesforce/salesforce-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'HubSpot', logo: 'https://www.vectorlogo.zone/logos/hubspot/hubspot-ar21.svg', bgColor: 'bg-orange-50' },
                  { name: 'WhatsApp', logo: 'https://www.vectorlogo.zone/logos/whatsapp/whatsapp-ar21.svg', bgColor: 'bg-green-50' },
                  { name: 'Meta', logo: 'https://www.vectorlogo.zone/logos/meta/meta-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Slack', logo: 'https://www.vectorlogo.zone/logos/slack/slack-ar21.svg', bgColor: 'bg-purple-50' },
                  { name: 'Odoo', logo: 'https://www.vectorlogo.zone/logos/odoo/odoo-ar21.svg', bgColor: 'bg-gray-50' },
                  { name: 'Zapier', logo: 'https://www.vectorlogo.zone/logos/zapier/zapier-ar21.svg', bgColor: 'bg-orange-50' },
                  { name: 'Make', logo: 'https://seeklogo.com/images/I/integromat-logo-2B5C771360-seeklogo.com.png', bgColor: 'bg-blue-50' },
                  { name: 'Instagram', logo: 'https://www.vectorlogo.zone/logos/instagram/instagram-ar21.svg', bgColor: 'bg-pink-50' },
                  { name: 'Wix', logo: 'https://www.vectorlogo.zone/logos/wix/wix-ar21.svg', bgColor: 'bg-gray-50' },
                ].map((item, i) => (
                  <div key={`dup-${i}`} className="flex-shrink-0 w-36 h-24 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] transform hover:scale-110 overflow-hidden">
                    <div className={`w-full h-full flex items-center justify-center ${item.bgColor} rounded-md p-2`}>
                      <img src={item.logo} alt={item.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Second Marquee - Right to Left - Slower - Automation Tools */}
            <div className="overflow-hidden relative py-8">
              <div className="flex space-x-8 whitespace-nowrap animate-marquee-slow-reverse">
                {/* First set of logos */}
                {[
                  { name: 'WordPress', logo: 'https://www.vectorlogo.zone/logos/wordpress/wordpress-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'WooCommerce', logo: 'https://www.vectorlogo.zone/logos/woocommerce/woocommerce-ar21.svg', bgColor: 'bg-purple-50' },
                  { name: 'PrestaShop', logo: 'https://www.vectorlogo.zone/logos/prestashop/prestashop-ar21.svg', bgColor: 'bg-gray-50' },
                  { name: 'Shopify', logo: 'https://www.vectorlogo.zone/logos/shopify/shopify-ar21.svg', bgColor: 'bg-green-50' },
                  { name: 'Trello', logo: 'https://www.vectorlogo.zone/logos/trello/trello-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'SendGrid', logo: 'https://www.vectorlogo.zone/logos/sendgrid/sendgrid-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Mailchimp', logo: 'https://www.vectorlogo.zone/logos/mailchimp/mailchimp-ar21.svg', bgColor: 'bg-yellow-50' },
                  { name: 'Gmail', logo: 'https://www.vectorlogo.zone/logos/gmail/gmail-ar21.svg', bgColor: 'bg-red-50' },
                  { name: 'Google Calendar', logo: 'https://www.vectorlogo.zone/logos/google_calendar/google_calendar-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Notion', logo: 'https://www.vectorlogo.zone/logos/notion/notion-ar21.svg', bgColor: 'bg-gray-50' },
                ].map((item, i) => (
                  <div key={i} className="flex-shrink-0 w-36 h-24 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] transform hover:scale-110 overflow-hidden">
                    <div className={`w-full h-full flex items-center justify-center ${item.bgColor} rounded-md p-2`}>
                      <img src={item.logo} alt={item.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  </div>
                ))}
                
                {/* Duplicate logos for infinite scroll effect */}
                {[
                  { name: 'WordPress', logo: 'https://www.vectorlogo.zone/logos/wordpress/wordpress-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'WooCommerce', logo: 'https://www.vectorlogo.zone/logos/woocommerce/woocommerce-ar21.svg', bgColor: 'bg-purple-50' },
                  { name: 'PrestaShop', logo: 'https://www.vectorlogo.zone/logos/prestashop/prestashop-ar21.svg', bgColor: 'bg-gray-50' },
                  { name: 'Shopify', logo: 'https://www.vectorlogo.zone/logos/shopify/shopify-ar21.svg', bgColor: 'bg-green-50' },
                  { name: 'Trello', logo: 'https://www.vectorlogo.zone/logos/trello/trello-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'SendGrid', logo: 'https://www.vectorlogo.zone/logos/sendgrid/sendgrid-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Mailchimp', logo: 'https://www.vectorlogo.zone/logos/mailchimp/mailchimp-ar21.svg', bgColor: 'bg-yellow-50' },
                  { name: 'Gmail', logo: 'https://www.vectorlogo.zone/logos/gmail/gmail-ar21.svg', bgColor: 'bg-red-50' },
                  { name: 'Google Calendar', logo: 'https://www.vectorlogo.zone/logos/google_calendar/google_calendar-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Notion', logo: 'https://www.vectorlogo.zone/logos/notion/notion-ar21.svg', bgColor: 'bg-gray-50' },
                ].map((item, i) => (
                  <div key={`dup-${i}`} className="flex-shrink-0 w-36 h-24 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] transform hover:scale-110 overflow-hidden">
                    <div className={`w-full h-full flex items-center justify-center ${item.bgColor} rounded-md p-2`}>
                      <img src={item.logo} alt={item.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Third Marquee - Left to Right - Medium Speed - Enterprise CRMs */}
            <div className="overflow-hidden relative py-8">
              <div className="flex space-x-8 whitespace-nowrap animate-marquee-medium">
                {/* First set of logos */}
                {[
                  { name: 'Google Drive', logo: 'https://www.vectorlogo.zone/logos/google_drive/google_drive-ar21.svg', bgColor: 'bg-yellow-50' },
                  { name: 'Airtable', logo: 'https://www.vectorlogo.zone/logos/airtable/airtable-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Discord', logo: 'https://www.vectorlogo.zone/logos/discordapp/discordapp-ar21.svg', bgColor: 'bg-indigo-50' },
                  { name: 'Webflow', logo: 'https://www.vectorlogo.zone/logos/webflow/webflow-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'ClickUp', logo: 'https://clickup.com/landing/images/clickup-logo-gradient.svg', bgColor: 'bg-gray-50' },
                  { name: 'Brevo', logo: 'https://www.brevo.com/images/logo/logo-brevo-blue.svg', bgColor: 'bg-blue-50' },
                  { name: 'Flowdesk', logo: 'https://flowdesk.io/wp-content/uploads/2022/05/logo.svg', bgColor: 'bg-gray-50' },
                  { name: 'Zendesk', logo: 'https://www.vectorlogo.zone/logos/zendesk/zendesk-ar21.svg', bgColor: 'bg-teal-50' },
                  { name: 'Stripe', logo: 'https://www.vectorlogo.zone/logos/stripe/stripe-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Twilio', logo: 'https://www.vectorlogo.zone/logos/twilio/twilio-ar21.svg', bgColor: 'bg-red-50' },
                ].map((item, i) => (
                  <div key={i} className="flex-shrink-0 w-36 h-24 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] transform hover:scale-110 overflow-hidden">
                    <div className={`w-full h-full flex items-center justify-center ${item.bgColor} rounded-md p-2`}>
                      <img src={item.logo} alt={item.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  </div>
                ))}
                
                {/* Duplicate logos for infinite scroll effect */}
                {[
                  { name: 'Google Drive', logo: 'https://www.vectorlogo.zone/logos/google_drive/google_drive-ar21.svg', bgColor: 'bg-yellow-50' },
                  { name: 'Airtable', logo: 'https://www.vectorlogo.zone/logos/airtable/airtable-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Discord', logo: 'https://www.vectorlogo.zone/logos/discordapp/discordapp-ar21.svg', bgColor: 'bg-indigo-50' },
                  { name: 'Webflow', logo: 'https://www.vectorlogo.zone/logos/webflow/webflow-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'ClickUp', logo: 'https://clickup.com/landing/images/clickup-logo-gradient.svg', bgColor: 'bg-gray-50' },
                  { name: 'Brevo', logo: 'https://www.brevo.com/images/logo/logo-brevo-blue.svg', bgColor: 'bg-blue-50' },
                  { name: 'Flowdesk', logo: 'https://flowdesk.io/wp-content/uploads/2022/05/logo.svg', bgColor: 'bg-gray-50' },
                  { name: 'Zendesk', logo: 'https://www.vectorlogo.zone/logos/zendesk/zendesk-ar21.svg', bgColor: 'bg-teal-50' },
                  { name: 'Stripe', logo: 'https://www.vectorlogo.zone/logos/stripe/stripe-ar21.svg', bgColor: 'bg-blue-50' },
                  { name: 'Twilio', logo: 'https://www.vectorlogo.zone/logos/twilio/twilio-ar21.svg', bgColor: 'bg-red-50' },
                ].map((item, i) => (
                  <div key={`dup-${i}`} className="flex-shrink-0 w-36 h-24 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-violet-500/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] transform hover:scale-110 overflow-hidden">
                    <div className={`w-full h-full flex items-center justify-center ${item.bgColor} rounded-md p-2`}>
                      <img src={item.logo} alt={item.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Integration features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-cyan-500/10 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 group-hover:border-violet-500/30 transition-all duration-300 h-full z-10">
                <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-300">
                  <Database className="w-8 h-8 text-violet-400 group-hover:animate-bounce-slow" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.integrations.features.crm.title')}</h3>
                <p className="text-gray-300 leading-relaxed">{t('pages.home.integrations.features.crm.description')}</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-cyan-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-cyan-500/10 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 group-hover:border-violet-500/30 transition-all duration-300 h-full z-10">
                <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-300">
                  <Layers className="w-8 h-8 text-cyan-400 group-hover:animate-bounce-slow" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.integrations.features.automation.title')}</h3>
                <p className="text-gray-300 leading-relaxed">{t('pages.home.integrations.features.automation.description')}</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-cyan-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-cyan-500/10 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 group-hover:border-violet-500/30 transition-all duration-300 h-full z-10">
                <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-300">
                  <Globe2 className="w-8 h-8 text-indigo-400 group-hover:animate-bounce-slow" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.integrations.features.web.title')}</h3>
                <p className="text-gray-300 leading-relaxed">{t('pages.home.integrations.features.web.description')}</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-cyan-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Subtle separator at bottom with animation */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-30 animate-pulse"></div>
      </section>

      {/* E-commerce Section - NEW */}
      <section className="relative py-28 overflow-hidden">
        {/* Modern background with mesh gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(124,58,237,0.1),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(45,212,191,0.1),transparent_60%)]" />
        
        {/* Interactive elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-violet-600/10 to-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '15s' }}></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-tr from-indigo-600/10 to-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '20s' }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
              <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
              E-commerce Solutions
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Streamline Your <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">Online Store</GradientText> Experience
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
              Integrate your e-commerce platform with our chatbot for a seamless shopping experience. Import products, track orders, and engage customers in real-time.
            </p>
          </div>

          {/* Main feature showcase */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-violet-600/30 to-cyan-500/30 blur-xl opacity-60"></div>
                <div className="relative bg-black/60 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl transform transition-transform hover:-translate-y-1 duration-300 overflow-hidden">
                  <div className="absolute top-0 right-0 z-10 bg-gradient-to-l from-cyan-500 to-violet-500 text-xs uppercase tracking-wider text-white font-semibold py-1 px-3 rounded-bl-lg">Live Preview</div>
                  
                  {/* Mockup of Product Management Interface */}
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-violet-400" />
                        <h3 className="text-white font-semibold">Product Management</h3>
                      </div>
                      <div className="flex gap-2">
                        <button className="bg-white/10 hover:bg-white/20 p-1 rounded">
                          <RefreshCw className="w-4 h-4 text-white" />
                        </button>
                        <button className="bg-white/10 hover:bg-white/20 p-1 rounded">
                          <Settings className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-4 mb-6">
                      {/* Product cards with animation */}
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white/5 backdrop-blur-xl rounded-lg p-4 border border-white/10 hover:border-violet-500/30 transition-all duration-300 flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-violet-600/20 to-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="w-6 h-6 text-violet-400" />
                          </div>
                          <div className="flex-grow min-w-0">
                            <div className="text-white font-medium truncate">Product {i}</div>
                            <div className="text-gray-400 text-sm">$99.99 • In stock</div>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                        </div>
                      ))}
                    </div>
                    
                    <button className="w-full bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-medium py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
                      <Plus className="w-4 h-4" />
                      Import More Products
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2 flex flex-col justify-center">
              <div className="space-y-8">
                <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                    <Store className="w-8 h-8 text-violet-400" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-4">Product Sync</h3>
                  <p className="text-gray-300 text-lg leading-relaxed">Automatically import products from Shopify, PrestaShop, WooCommerce and more. Keep inventory and prices synced in real-time.</p>
                </div>

                <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                    <Package className="w-8 h-8 text-cyan-400" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-4">Order Tracking</h3>
                  <p className="text-gray-300 text-lg leading-relaxed">Let customers check order status and delivery updates directly through your chatbot. Reduce support queries and enhance customer satisfaction.</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Additional features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                  <ShoppingCart className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="text-xl font-semibold text-white">Cart Recovery</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Automatically follow up with customers who abandoned their shopping carts and boost your conversion rates.</p>
            </div>
            
            <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                  <Gift className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white">Promotions</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Create personalized promotions and discounts based on customer behavior and preferences.</p>
            </div>
            
            <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shadow-inner">
                  <Users className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="text-xl font-semibold text-white">Customer Profiles</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Build comprehensive customer profiles with purchase history and preferences for personalized recommendations.</p>
            </div>
          </div>
        </div>
        
        {/* Subtle separator at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent opacity-30"></div>
      </section>

      {/* Linktree Section */}
      <section className="relative py-28 overflow-hidden">
        {/* Fond moderne avec mesh gradient et motif */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.08),transparent_70%)]" />
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-[0.03]" />
        
        {/* Effets de cercles lumineux en arrière-plan */}
        <div className="absolute top-1/3 right-1/4 w-[350px] h-[350px] rounded-full bg-gradient-to-br from-violet-500/10 to-transparent blur-3xl" />
        <div className="absolute bottom-1/3 left-1/4 w-[300px] h-[300px] rounded-full bg-gradient-to-tr from-cyan-500/10 to-transparent blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80">
              <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
              Interactive Links
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              {t('pages.home.linktree.title')}{' '}
              <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-400">{t('pages.home.linktree.highlight')}</GradientText>{' '}
              {t('pages.home.linktree.suffix')}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-16">
              {t('pages.home.linktree.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                <Share2 className="w-8 h-8 text-violet-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.linktree.features.sharing.title')}</h3>
              <p className="text-gray-300 text-lg leading-relaxed">{t('pages.home.linktree.features.sharing.description')}</p>
            </div>

            <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                <Rocket className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.linktree.features.conversion.title')}</h3>
              <p className="text-gray-300 text-lg leading-relaxed">{t('pages.home.linktree.features.conversion.description')}</p>
            </div>

            <div className="group bg-black/40 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-6 shadow-inner">
                <LineChart className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">{t('pages.home.linktree.features.analytics.title')}</h3>
              <p className="text-gray-300 text-lg leading-relaxed">{t('pages.home.linktree.features.analytics.description')}</p>
            </div>
          </div>
        </div>
        
        {/* Élément de décoration final */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-violet-600 to-cyan-500 rounded-full blur-sm"></div>
      </section>

      <Footer />
    </div>
  );
}